import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { formatDate } from '../../utils/formatDate';

const GRID_COLUMNS = {
  xs: '1fr',
  sm: '1.5fr 1fr 0.7fr',
  md: '1.5fr 1fr 1.2fr 0.6fr 0.7fr 1fr 0.7fr',
};

const EMPTY_FORM = {
  plan_name: '',
  plan_desc: '',
  service_type: '',
  billing_plan_type: 'Subscription_tiers',
  stripe_product_id: '',
  plan_details: '{}',
  custom_plan: false,
};

const Planes = () => {
  // State
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterServiceType, setFilterServiceType] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Fetch plans
  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterServiceType) {
        params.service_type = filterServiceType;
      }
      if (showInactive) {
        params.include_inactive = true;
      }

      const response = await api.get('/admin/billing-plans', { params });
      setPlans(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching billing plans:', err);
      setError('Error al cargar los planes. Inténtalo de nuevo.');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [filterServiceType, showInactive]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Dialog handlers
  const handleOpenCreate = () => {
    setDialogMode('create');
    setEditingPlanId(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setDialogMode('edit');
    setEditingPlanId(plan.billing_plan_id);
    setForm({
      plan_name: plan.plan_name || '',
      plan_desc: plan.plan_desc || '',
      service_type: plan.service_type || '',
      billing_plan_type: plan.billing_plan_type || 'Subscription_tiers',
      stripe_product_id: plan.stripe_product_id || '',
      plan_details: plan.plan_details
        ? (typeof plan.plan_details === 'string' ? plan.plan_details : JSON.stringify(plan.plan_details, null, 2))
        : '{}',
      custom_plan: plan.custom_plan || false,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlanId(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
  };

  const handleFormChange = (field) => (e) => {
    const value = field === 'custom_plan' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Validate JSON
  const isValidJSON = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  // Save plan
  const handleSave = async () => {
    // Validation
    if (!form.plan_name.trim()) {
      setFormError('El nombre del plan es obligatorio.');
      return;
    }
    if (!form.service_type) {
      setFormError('El tipo de servicio es obligatorio.');
      return;
    }
    if (form.plan_details && !isValidJSON(form.plan_details)) {
      setFormError('El campo plan_details debe ser un JSON válido.');
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      const body = {
        plan_name: form.plan_name.trim(),
        plan_desc: form.plan_desc.trim(),
        service_type: form.service_type,
        billing_plan_type: form.billing_plan_type,
        stripe_product_id: form.stripe_product_id.trim(),
        plan_details: form.plan_details ? JSON.parse(form.plan_details) : {},
        custom_plan: form.custom_plan,
      };

      if (dialogMode === 'create') {
        await api.post('/admin/billing-plans', body);
        Swal.fire({
          icon: 'success',
          title: 'Plan creado',
          text: 'El plan se ha creado correctamente.',
          confirmButtonColor: '#0F52BD',
        });
      } else {
        await api.put(`/admin/billing-plans/${editingPlanId}`, body);
        Swal.fire({
          icon: 'success',
          title: 'Plan actualizado',
          text: 'El plan se ha actualizado correctamente.',
          confirmButtonColor: '#0F52BD',
        });
      }

      handleCloseDialog();
      fetchPlans();
    } catch (err) {
      console.error('Error saving plan:', err);
      setFormError(err.response?.data?.message || 'Error al guardar el plan.');
    } finally {
      setSaving(false);
    }
  };

  // Delete plan
  const handleDelete = async (plan) => {
    const result = await Swal.fire({
      title: '¿Eliminar plan?',
      text: `Estás a punto de eliminar "${plan.plan_name}". Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F44336',
      cancelButtonColor: '#666',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/billing-plans/${plan.billing_plan_id}`);
        Swal.fire({
          icon: 'success',
          title: 'Plan eliminado',
          text: 'El plan se ha eliminado correctamente.',
          confirmButtonColor: '#0F52BD',
        });
        fetchPlans();
      } catch (err) {
        console.error('Error deleting plan:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'No se pudo eliminar el plan.',
          confirmButtonColor: '#0F52BD',
        });
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: '12px 16px', sm: '8px 16px', md: '12px 16px' },
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Left: Title */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              ml: { xs: '50px', sm: '50px', md: 35.5 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '22px' },
                fontWeight: 'medium',
                color: '#000',
              }}
            >
              Planes de Billing
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: '12px', sm: '15px', md: '14px' },
                color: '#666',
                mt: { xs: 0, md: 0.3 },
                display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
              }}
            >
              Gestiona los planes de facturación de la plataforma
            </Typography>
          </Box>

          {/* Right: New Plan button */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1.5, sm: 2 },
              mr: { xs: 1.5, md: 5 },
              alignItems: 'center',
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: { sm: '18px', md: '16px' } }} />}
              onClick={handleOpenCreate}
              sx={{
                textTransform: 'none',
                fontWeight: 'medium',
                backgroundColor: '#0F52BD',
                fontSize: { sm: '12px', md: '12px' },
                px: { sm: 1.5, md: 1.5 },
                py: { sm: 0.8, md: 0.8 },
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#0a3d8f' },
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              Nuevo Plan
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Spacer for fixed header */}
      <Box
        sx={{
          height: { xs: '65px', sm: '70px', md: '100px', lg: '90px' },
          '@media (max-width: 600px) and (min-resolution: 1.25dppx)': { height: '65px' },
          '@media (max-width: 600px) and (min-resolution: 1.5dppx)': { height: '90px' },
          '@media (min-width: 600px) and (min-resolution: 1.25dppx)': { height: '75px' },
          '@media (min-width: 900px) and (min-resolution: 1.25dppx)': { height: '95px' },
        }}
      />

      {/* Main content */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}>
          {/* Error state */}
          {error && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#c62828', fontSize: '14px' }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Loading state */}
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: '#0F52BD' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Cargando planes...
              </Typography>
            </Box>
          ) : plans.length === 0 ? (
            /* Empty state */
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                borderRadius: 2.5,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: '#000', fontWeight: 'medium' }}
              >
                No se encontraron planes
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#666', mb: 3 }}
              >
                {filterServiceType || showInactive
                  ? 'No hay planes que coincidan con los filtros aplicados.'
                  : 'Aún no hay planes registrados en la plataforma.'}
              </Typography>
              {!filterServiceType && !showInactive && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreate}
                  sx={{
                    backgroundColor: '#0F52BD',
                    '&:hover': { backgroundColor: '#0a3d8f' },
                    textTransform: 'none',
                    fontWeight: 'medium',
                    px: 3,
                    py: 1.5,
                    borderRadius: '8px',
                  }}
                >
                  Crear Primer Plan
                </Button>
              )}
            </Paper>
          ) : (
            /* Data Card */
            <Paper
              elevation={0}
              sx={{
                pt: 4,
                pl: 4,
                pr: 3,
                pb: 3,
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                borderRadius: 2.5,
                overflow: 'hidden',
              }}
            >
              {/* Card header: Icon + Title */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CreditCardIcon sx={{ fontSize: 28, mr: 1, color: '#000' }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ fontSize: '18px', color: '#000' }}
                  >
                    Planes
                  </Typography>
                </Box>
              </Box>

              {/* Filters row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <FormControl size="small" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <InputLabel>Tipo de servicio</InputLabel>
                  <Select
                    value={filterServiceType}
                    onChange={(e) => setFilterServiceType(e.target.value)}
                    label="Tipo de servicio"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Widget">Widget</MenuItem>
                    <MenuItem value="Analyser">Analyser</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F52BD' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F52BD' },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                      Mostrar inactivos
                    </Typography>
                  }
                />
              </Box>

              {/* Grid data list */}
              <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                {/* Grid Headers - desktop only */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: GRID_COLUMNS.md,
                    gap: 2,
                    py: 1,
                    borderBottom: '2px solid #E0E0E0',
                    mb: 1,
                    alignItems: 'center',
                  }}
                >
                  {['Nombre', 'Tipo servicio', 'Tipo plan', 'Custom', 'Estado', 'Fecha creación', 'Acciones'].map((label) => (
                    <Typography
                      key={label}
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: '#000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>

                {/* Grid Rows */}
                <Stack spacing={0}>
                  {plans.map((plan) => (
                    <Box
                      key={plan.billing_plan_id}
                      sx={{
                        display: { xs: 'block', sm: 'grid' },
                        gridTemplateColumns: { sm: GRID_COLUMNS.sm, md: GRID_COLUMNS.md },
                        gap: 2,
                        py: 2,
                        alignItems: 'center',
                        borderBottom: '1px solid #E0E0E0',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: '#f5f8ff' },
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      {/* Mobile view - stacked card */}
                      <Box
                        sx={{
                          display: { xs: 'block', sm: 'none' },
                          p: 2,
                          border: '1px solid #F0F0F0',
                          borderRadius: '8px',
                          backgroundColor: '#FAFAFA',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                            {plan.plan_name || '-'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(plan)}
                              sx={{ color: '#0F52BD' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(plan)}
                              sx={{ color: '#F44336' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '14px', color: '#666', mb: 1 }}>
                          {plan.service_type || '-'} &middot; {plan.billing_plan_type || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Chip
                            label={plan.status ? 'Activo' : 'Inactivo'}
                            size="small"
                            sx={{
                              backgroundColor: plan.status ? '#e8f5e9' : '#ffebee',
                              color: plan.status ? '#2e7d32' : '#c62828',
                              border: `1px solid ${plan.status ? '#4caf50' : '#f44336'}`,
                              fontWeight: 'medium',
                              fontSize: '12px',
                            }}
                          />
                          {plan.custom_plan && (
                            <Chip label="Custom" size="small" sx={{ fontSize: '12px', backgroundColor: '#f5f5f5' }} />
                          )}
                        </Box>
                      </Box>

                      {/* Tablet view - 3 columns */}
                      <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'medium', fontSize: '14px' }}>
                          {plan.plan_name || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {plan.service_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
                        <Chip
                          label={plan.status ? 'Activo' : 'Inactivo'}
                          size="small"
                          sx={{
                            backgroundColor: plan.status ? '#e8f5e9' : '#ffebee',
                            color: plan.status ? '#2e7d32' : '#c62828',
                            border: `1px solid ${plan.status ? '#4caf50' : '#f44336'}`,
                            fontWeight: 'medium',
                            fontSize: '12px',
                          }}
                        />
                      </Box>

                      {/* Desktop view - 7 columns */}
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'medium', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {plan.plan_name || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {plan.service_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {plan.billing_plan_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {plan.custom_plan ? 'Sí' : 'No'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <Chip
                          label={plan.status ? 'Activo' : 'Inactivo'}
                          size="small"
                          sx={{
                            backgroundColor: plan.status ? '#e8f5e9' : '#ffebee',
                            color: plan.status ? '#2e7d32' : '#c62828',
                            border: `1px solid ${plan.status ? '#4caf50' : '#f44336'}`,
                            fontWeight: 'medium',
                            fontSize: '12px',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#666' }}>
                          {formatDate(plan.creation_date)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(plan)}
                          sx={{ color: '#0F52BD', '&:hover': { backgroundColor: '#e8eef7' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(plan)}
                          sx={{ color: '#F44336', '&:hover': { backgroundColor: '#ffebee' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            boxShadow: '0px 2px 25px 0px rgba(98, 113, 137, 0.46)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            fontSize: '18px',
            color: '#000',
            borderBottom: '1px solid #E0E0E0',
            pb: 2,
          }}
        >
          {dialogMode === 'create' ? 'Nuevo Plan' : 'Editar Plan'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          {formError && (
            <Typography sx={{ color: '#c62828', fontSize: '14px', mb: 2 }}>
              {formError}
            </Typography>
          )}

          <TextField
            label="Nombre del plan"
            value={form.plan_name}
            onChange={handleFormChange('plan_name')}
            fullWidth
            required
            sx={{ mb: 2.5 }}
          />

          <TextField
            label="Descripción"
            value={form.plan_desc}
            onChange={handleFormChange('plan_desc')}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2.5 }}
          />

          <FormControl fullWidth sx={{ mb: 2.5 }} required>
            <InputLabel>Tipo de servicio</InputLabel>
            <Select
              value={form.service_type}
              onChange={handleFormChange('service_type')}
              label="Tipo de servicio"
            >
              <MenuItem value="Widget">Widget</MenuItem>
              <MenuItem value="Analyser">Analyser</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Tipo de plan de billing</InputLabel>
            <Select
              value={form.billing_plan_type}
              onChange={handleFormChange('billing_plan_type')}
              label="Tipo de plan de billing"
            >
              <MenuItem value="Subscription_tiers">Subscription_tiers</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Stripe Product ID"
            value={form.stripe_product_id}
            onChange={handleFormChange('stripe_product_id')}
            fullWidth
            sx={{ mb: 2.5 }}
          />

          <TextField
            label="Plan Details (JSON)"
            value={form.plan_details}
            onChange={handleFormChange('plan_details')}
            fullWidth
            multiline
            rows={5}
            sx={{
              mb: 2.5,
              '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '13px' },
            }}
            error={form.plan_details !== '' && !isValidJSON(form.plan_details)}
            helperText={
              form.plan_details !== '' && !isValidJSON(form.plan_details)
                ? 'JSON no válido'
                : ''
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.custom_plan}
                onChange={handleFormChange('custom_plan')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F52BD' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F52BD' },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                Plan personalizado (custom)
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid #E0E0E0', pt: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              color: '#666',
              borderRadius: '8px',
              fontWeight: 'medium',
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none',
              backgroundColor: '#0F52BD',
              borderRadius: '8px',
              fontWeight: 'medium',
              px: 3,
              '&:hover': { backgroundColor: '#0a3d8f' },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : dialogMode === 'create' ? (
              'Crear Plan'
            ) : (
              'Guardar Cambios'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Planes;
